import { useState, useRef, useCallback, useEffect } from "react";
import { RealtimeClient } from "@speechmatics/real-time-client";
import { RealtimeTokenResponse } from "../api/aiSpeechToTextApi";

interface UseSpeechToTextOptions {
  /** Fetch a short-lived Speechmatics JWT from the API */
  getRealtimeToken: () => Promise<RealtimeTokenResponse | "RATE_LIMITED" | "FORBIDDEN" | null>;
  /** Language code for transcription (default: 'en') */
  language?: string;
  /** Enable partial results (default: true) */
  enablePartials?: boolean;
  /** Maximum delay for partial results in seconds (default: 1) */
  maxDelay?: number;
  /** Callback when transcription results are received */
  onResult?: (transcript: string, isFinal: boolean) => void;
}

interface UseSpeechToTextReturn {
  isRecording: boolean;
  error: string | null;
  interimResult: string;
  results: string[];
  startSpeechToText: () => Promise<void>;
  stopSpeechToText: () => Promise<void>;
}

const useSpeechToText = (
  options: UseSpeechToTextOptions,
): UseSpeechToTextReturn => {
  const {
    getRealtimeToken,
    language = "en",
    enablePartials = true,
    maxDelay = 1,
    onResult,
  } = options;

  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [interimResult, setInterimResult] = useState("");
  const [results, setResults] = useState<string[]>([]);
  const [streamingTranscript, setStreamingTranscript] = useState("");

  const clientRef = useRef<RealtimeClient | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const finalizedTranscriptRef = useRef<string>("");
  const currentPartialRef = useRef<string>("");
  const isRecordingRef = useRef<boolean>(false);
  const messageHandlerRef = useRef<((event: any) => void) | null>(null);

  const buildFullTranscript = (finalized: string, partial: string): string => {
    if (!finalized && !partial) return "";
    if (!finalized) return partial;
    if (!partial) return finalized;

    const needsSpace = !finalized.endsWith(" ") && !partial.startsWith(" ");
    return finalized + (needsSpace ? " " : "") + partial;
  };

  const cleanup = useCallback(async () => {
    try {
      if (processorRef.current) {
        processorRef.current.disconnect();
        processorRef.current = null;
      }

      if (gainNodeRef.current) {
        gainNodeRef.current.disconnect();
        gainNodeRef.current = null;
      }

      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        await audioContextRef.current.close();
        audioContextRef.current = null;
      }

      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }

      if (clientRef.current) {
        if (messageHandlerRef.current) {
          clientRef.current.removeEventListener(
            "receiveMessage",
            messageHandlerRef.current,
          );
          messageHandlerRef.current = null;
        }
        try {
          await clientRef.current.stopRecognition();
        } catch (err) {
          console.error("Error stopping recognition:", err);
        }
        clientRef.current = null;
      }

      setIsRecording(false);
      isRecordingRef.current = false;
      setInterimResult("");
      setStreamingTranscript("");
      finalizedTranscriptRef.current = "";
      currentPartialRef.current = "";
    } catch (err) {
      console.error("Error during cleanup:", err);
    }
  }, []);

  const startSpeechToText = useCallback(async () => {
    if (isRecording) {
      return;
    }

    try {
      setError(null);
      setInterimResult("");
      setResults([]);
      setStreamingTranscript("");
      finalizedTranscriptRef.current = "";
      currentPartialRef.current = "";

      const tokenResult = await getRealtimeToken();
      if (tokenResult === "RATE_LIMITED") {
        setError("You have reached the maximum number of recording sessions per hour.");
        return;
      }
      if (tokenResult === "FORBIDDEN" || tokenResult === null) {
        setError("Voice recording is not available.");
        return;
      }

      const client = new RealtimeClient({
        url: tokenResult.websocketUrl,
      });

      clientRef.current = client;

      const handleReceiveMessage = (event: any) => {
        const message = event.data;

        if (message.message === "AddTranscript") {
          const transcript = message.metadata?.transcript || "";
          if (transcript) {
            const trimmedTranscript = transcript.trim();

            if (trimmedTranscript) {
              if (finalizedTranscriptRef.current) {
                const needsSpace =
                  !finalizedTranscriptRef.current.endsWith(" ") &&
                  !trimmedTranscript.startsWith(" ");
                finalizedTranscriptRef.current +=
                  (needsSpace ? " " : "") + trimmedTranscript;
              } else {
                finalizedTranscriptRef.current = trimmedTranscript;
              }
            }

            currentPartialRef.current = "";

            const fullTranscript = buildFullTranscript(
              finalizedTranscriptRef.current,
              currentPartialRef.current,
            );
            setStreamingTranscript(fullTranscript);
            setInterimResult(fullTranscript);
            setResults((prev) => [...prev, trimmedTranscript]);

            if (onResult) {
              onResult(fullTranscript, false);
            }
          }
        } else if (message.message === "AddPartialTranscript") {
          const transcript = message.metadata?.transcript || "";
          if (transcript) {
            currentPartialRef.current = transcript;

            const fullTranscript = buildFullTranscript(
              finalizedTranscriptRef.current,
              currentPartialRef.current,
            );
            setStreamingTranscript(fullTranscript);
            setInterimResult(fullTranscript);

            if (onResult) {
              onResult(fullTranscript, false);
            }
          }
        } else if (message.message === "EndOfTranscript") {
          if (currentPartialRef.current) {
            const trimmedPartial = currentPartialRef.current.trim();
            if (trimmedPartial) {
              if (finalizedTranscriptRef.current) {
                const needsSpace =
                  !finalizedTranscriptRef.current.endsWith(" ") &&
                  !trimmedPartial.startsWith(" ");
                finalizedTranscriptRef.current +=
                  (needsSpace ? " " : "") + trimmedPartial;
              } else {
                finalizedTranscriptRef.current = trimmedPartial;
              }
            }
            currentPartialRef.current = "";
          }

          setStreamingTranscript(finalizedTranscriptRef.current);
          setInterimResult(finalizedTranscriptRef.current);

          if (finalizedTranscriptRef.current && onResult) {
            onResult(finalizedTranscriptRef.current, true);
          }
        } else if (message.message === "Error") {
          setError(message.reason || "Transcription error occurred");
          console.error("Speechmatics error:", message);
        }
      };

      messageHandlerRef.current = handleReceiveMessage;
      client.addEventListener("receiveMessage", handleReceiveMessage);

      await client.start(tokenResult.jwt, {
        transcription_config: {
          language,
          enable_partials: enablePartials,
          max_delay: maxDelay,
        },
        audio_format: {
          type: "raw",
          encoding: "pcm_f32le",
          sample_rate: 16000,
        },
      });

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      mediaStreamRef.current = stream;

      const audioContext = new (
        window.AudioContext || (window as any).webkitAudioContext
      )({
        sampleRate: 16000,
      });
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);

      processor.onaudioprocess = (e) => {
        if (!clientRef.current || !isRecordingRef.current) {
          return;
        }

        const inputData = e.inputBuffer.getChannelData(0);
        const float32Data = new Float32Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          float32Data[i] = inputData[i];
        }

        try {
          clientRef.current.sendAudio(float32Data.buffer);
        } catch (err) {
          console.error("Error sending audio:", err);
        }
      };

      source.connect(processor);
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 0;
      processor.connect(gainNode);
      gainNode.connect(audioContext.destination);
      processorRef.current = processor;
      gainNodeRef.current = gainNode;

      setIsRecording(true);
      isRecordingRef.current = true;
    } catch (err: any) {
      setError(err.message || "Failed to start speech-to-text");
      console.error("Error starting speech-to-text:", err);
      await cleanup();
    }
  }, [
    language,
    enablePartials,
    maxDelay,
    isRecording,
    onResult,
    cleanup,
    getRealtimeToken,
  ]);

  const stopSpeechToText = useCallback(async () => {
    if (!isRecordingRef.current) {
      return;
    }

    await cleanup();
  }, [cleanup]);

  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    isRecording,
    error,
    interimResult: streamingTranscript || interimResult,
    results,
    startSpeechToText,
    stopSpeechToText,
  };
};

export default useSpeechToText;
